import { useCallback, useEffect, useState, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useWebSocket, useStreamingMessages } from '@/hooks/useWebSocket'
import { messageStreamStarted } from '@/store/slices/socketSlice'
import { 
  setActiveSession,
  addOptimisticMessage,
  removeOptimisticMessage,
  startStreamingMessage,
  updateStreamingMessage,
  streamingError,
  cancelStreaming,
  fetchOpsChatSessions,
  loadOpsChatSession,
  createOpsChatSession,
  sendOpsMessage
} from '@/store/slices/opsChatSlice'
import { OpsMessage } from '@/store/slices/opsChatSlice'
import { v4 as uuidv4 } from 'uuid'

interface UseOpsChatStreamingOptions {
  autoConnect?: boolean
}

export const useOpsChatStreaming = (options: UseOpsChatStreamingOptions = {}) => {
  const { autoConnect = true } = options
  const dispatch = useAppDispatch()
  
  // Redux state
  const {
    sessions,
    activeSessionId,
    streaming,
    loadingSessions,
    sessionsError
  } = useAppSelector(state => state.opsChat)
  
  const activeSession = activeSessionId ? sessions[activeSessionId] : null
  
  // WebSocket hooks
  const webSocket = useWebSocket({ 
    autoConnect, 
    sessionId: activeSessionId || undefined 
  })
  
  const streamingMessages = useStreamingMessages(activeSessionId || '')
  
  // Local state
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize WebSocket connection and load initial data
  useEffect(() => {
    const initialize = async () => {
      try {
        // Connect WebSocket first
        if (autoConnect) {
          await webSocket.connect()
        }
        
        // Load chat sessions
        await dispatch(fetchOpsChatSessions(10)).unwrap()
        setIsInitialized(true)
      } catch (error) {
        console.error('Failed to initialize Ops chat:', error)
        setIsInitialized(true) // Set to true even on error to prevent infinite loops
      }
    }

    if (!isInitialized) {
      initialize()
    }
  }, [dispatch, isInitialized, autoConnect])

  // Auto-join session when active session changes (but not for temp sessions)
  useEffect(() => {
    if (activeSessionId && 
        !activeSessionId.startsWith('temp-session-') && 
        webSocket.isConnected()) {
      webSocket.joinSession(activeSessionId)
    }
  }, [activeSessionId])

  // Handle streaming events from socket
  const processedMessagesRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!activeSessionId) return

    streamingMessages.streamingMessages.forEach(streamMsg => {
      const messageKey = `${streamMsg.sessionId}-${streamMsg.id}`
      
      if (streamMsg.sessionId === activeSessionId && !processedMessagesRef.current.has(messageKey)) {
        processedMessagesRef.current.add(messageKey)
        
        // For ops agent, we expect PowerBI data citations instead of document citations
        const metadata = {
          powerbi_queries: streamMsg.citations.map(citation => ({
            query_type: citation.documentId.split('_')[0] || 'unknown',
            dataset: citation.documentTitle,
            row_count: 0, // Will be populated from actual data
            execution_time_ms: 0
          })),
          confidence: 0.9 // Default confidence for PowerBI responses
        }
        
        dispatch(updateStreamingMessage({
          sessionId: activeSessionId,
          messageId: streamMsg.id,
          content: streamMsg.content,
          isComplete: streamMsg.isComplete,
          metadata
        }))
      }
    })
  }, [streamingMessages.streamingMessages.length, activeSessionId])

  // Send a message with streaming support
  const sendMessage = useCallback(async (content: string, sessionId?: string | null) => {
    try {
      const targetSessionId = sessionId || activeSessionId
      const messageId = uuidv4()
      const userMessageId = uuidv4()

      // If no session, create a temporary one
      let workingSessionId = targetSessionId || `temp-session-${Date.now()}`
      if (!targetSessionId) {
        dispatch(setActiveSession(workingSessionId))
      }

      // Add optimistic user message
      const userMessage: OpsMessage = {
        id: userMessageId,
        role: 'USER',
        content,
        createdAt: new Date().toISOString()
      }

      dispatch(addOptimisticMessage({ 
        sessionId: workingSessionId, 
        message: userMessage 
      }))

      // Check if streaming is enabled and WebSocket is connected
      if (streaming.enabled && webSocket.isConnected()) {
        // Use flushSync to ensure both streaming states are set synchronously
        // This bypasses React 18's automatic batching to show loading indicator immediately
        flushSync(() => {
          // Create immediate loading indicator with the EXACT messageId the server will use
          dispatch(startStreamingMessage({ 
            sessionId: workingSessionId, 
            messageId 
          }))
          
          // IMMEDIATELY create streaming message in socketSlice for hasActiveStreams
          dispatch(messageStreamStarted({
            messageId,
            sessionId: workingSessionId,
            timestamp: new Date()
          }))
        })
        
        // Send message via WebSocket with OPS agent type (include the messageId so server uses it)
        webSocket.sendMessage({
          sessionId: workingSessionId,
          content,
          role: 'USER',
          agentType: 'OPS',
          messageId // Tell server to use this exact messageId
        })

      } else {
        // Fallback to traditional HTTP request
        try {
          const response = await dispatch(sendOpsMessage({
            content,
            sessionId: targetSessionId || null
          })).unwrap()

          // Remove optimistic message since the thunk adds the real ones
          dispatch(removeOptimisticMessage({ 
            sessionId: workingSessionId, 
            messageId: userMessageId 
          }))

          // Update active session if needed
          if (!targetSessionId && (response as any).sessionId) {
            dispatch(setActiveSession((response as any).sessionId))
          }
        } catch (error) {
          // Remove optimistic message on error
          dispatch(removeOptimisticMessage({ 
            sessionId: workingSessionId, 
            messageId: userMessageId 
          }))
          throw error
        }
      }

    } catch (error) {
      console.error('Failed to send Ops message:', error)
      throw error
    }
  }, [
    activeSessionId, 
    dispatch, 
    streaming.enabled, 
    webSocket
  ])

  // Cancel streaming message
  const cancelMessage = useCallback((messageId: string) => {
    webSocket.cancelMessage(messageId)
    dispatch(cancelStreaming({ messageId }))
  }, [webSocket, dispatch])

  // Load a specific chat session
  const loadSession = useCallback(async (sessionId: string) => {
    try {
      await dispatch(loadOpsChatSession(sessionId)).unwrap()
      dispatch(setActiveSession(sessionId))
    } catch (error) {
      console.error('Failed to load Ops session:', error)
      throw error
    }
  }, [dispatch])

  // Start a new chat
  const startNewChat = useCallback(async () => {
    if (activeSessionId && 
        !activeSessionId.startsWith('temp-session-') && 
        webSocket.isConnected()) {
      webSocket.leaveSession(activeSessionId)
    }
    
    try {
      // Create a new session via API
      const newSession = await dispatch(createOpsChatSession()).unwrap()
      dispatch(setActiveSession(newSession.id))
    } catch (error) {
      // Fallback to temp session if API fails
      dispatch(setActiveSession(null))
    }
  }, [activeSessionId, dispatch, webSocket])

  // Send typing indicator
  const sendTyping = useCallback((isTyping: boolean) => {
    if (activeSessionId && webSocket.isConnected()) {
      webSocket.sendTyping(isTyping, activeSessionId)
    }
  }, [activeSessionId, webSocket])

  // Connection management
  const connect = useCallback(async () => {
    try {
      await webSocket.connect()
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      throw error
    }
  }, [webSocket])

  const disconnect = useCallback(() => {
    webSocket.disconnect()
  }, [webSocket])

  // Get current session state
  const getSessionState = useCallback(() => {
    if (!activeSession) {
      return {
        session: null,
        messages: [],
        isLoading: false,
        hasActiveStreams: false,
        typingUsers: []
      }
    }

    // Use persisted messages as the primary source
    const persistedMessages = activeSession.messages || []
    
    // Only include streaming messages that don't have a corresponding persisted message
    const persistedMessageIds = new Set(persistedMessages.map(msg => msg.id))
    const currentStreamingMessages = streamingMessages.streamingMessages
      .filter(msg => msg.sessionId === activeSessionId && !persistedMessageIds.has(msg.id))
      .map(streamMsg => ({
        id: streamMsg.id,
        role: 'ASSISTANT' as const,
        content: streamMsg.content,
        createdAt: streamMsg.timestamp,
        isStreaming: streamMsg.isStreaming,
        error: streamMsg.hasError ? 'Error generating response' : undefined,
        metadata: {
          powerbi_queries: streamMsg.citations.map(citation => ({
            query_type: citation.documentId.split('_')[0] || 'unknown',
            dataset: citation.documentTitle,
            row_count: 0,
            execution_time_ms: 0
          })),
          confidence: 0.9
        }
      }))

    // Combine and sort messages by timestamp
    const allMessages = [...persistedMessages, ...currentStreamingMessages]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

    return {
      session: activeSession,
      messages: allMessages,
      isLoading: activeSession.isLoading,
      hasActiveStreams: streamingMessages.hasActiveStreams,
      typingUsers: streamingMessages.typingUsers
    }
  }, [activeSession, activeSessionId, streamingMessages])

  return {
    // State
    sessions: Object.values(sessions),
    activeSession,
    activeSessionId,
    isInitialized,
    loadingSessions,
    sessionsError,
    
    // Streaming state
    streamingEnabled: streaming.enabled,
    currentStreamingMessage: streaming.currentStreamingMessage,
    hasActiveStreams: streamingMessages.hasActiveStreams,
    typingUsers: streamingMessages.typingUsers,
    
    // Connection state
    isConnected: webSocket.isConnected(),
    
    // Actions
    sendMessage,
    cancelMessage,
    loadSession,
    startNewChat,
    sendTyping,
    connect,
    disconnect,
    
    // Utilities
    getSessionState
  }
}

export default useOpsChatStreaming